import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  alpha,
  useTheme,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Event as EventIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Done as DoneIcon,
  Cancel as CancelIcon,
  Group as TotalLeadsIcon,
  TrendingUp as ActiveLeadsIcon,
  EventAvailable as ConfirmedIcon,
  Whatshot as HotLeadsIcon,
  Info as DetailsIcon,
  CalendarToday as ScheduledVisitIcon,
  NotInterested as NotInterestedIcon,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useNavigate } from 'react-router-dom';
import api from '../../../../services/api';

dayjs.extend(isoWeek);

const SiteVisits = ({ agentId }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('This Month');
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(dayjs());
  const [customEndDate, setCustomEndDate] = useState(dayjs());
  const [dateRange, setDateRange] = useState(() => {
    const today = dayjs();
    return {
      startDate: today.startOf('month').format('YYYY-MM-DD'),
      endDate: today.format('YYYY-MM-DD')
    };
  });
  
  // Status counts state
  const [statusCounts, setStatusCounts] = useState({});
  const [loadingStatusCounts, setLoadingStatusCounts] = useState(false);
  
  // Lead performance state
  const [leadPerformance, setLeadPerformance] = useState({});
  const [loadingLeadPerformance, setLoadingLeadPerformance] = useState(false);

  useEffect(() => {
    fetchStatusCounts();
    fetchLeadPerformance();
  }, [agentId, dateRange]);

  // Calculate date range based on filter
  const calculateDateRange = (filter) => {
    const today = dayjs();
    let startDate, endDate;

    switch (filter) {
      case 'Today':
        startDate = today.startOf('day').format('YYYY-MM-DD');
        endDate = today.endOf('day').format('YYYY-MM-DD');
        break;
      case 'This Week':
        startDate = today.startOf('isoWeek').format('YYYY-MM-DD');
        endDate = today.endOf('day').format('YYYY-MM-DD');
        break;
      case 'This Month':
        startDate = today.startOf('month').format('YYYY-MM-DD');
        endDate = today.endOf('day').format('YYYY-MM-DD');
        break;
      case 'This Quarter':
        const currentQuarter = Math.floor(today.month() / 3);
        const quarterStartMonth = currentQuarter * 3;
        startDate = today.month(quarterStartMonth).startOf('month').format('YYYY-MM-DD');
        endDate = today.endOf('day').format('YYYY-MM-DD');
        break;
      case 'Custom':
        return dateRange;
      default:
        startDate = today.startOf('month').format('YYYY-MM-DD');
        endDate = today.endOf('day').format('YYYY-MM-DD');
        break;
    }

    return { startDate, endDate };
  };

  // Get formatted date range display
  const getDateRangeDisplay = () => {
    if (!dateRange.startDate || !dateRange.endDate) return '';
    const start = dayjs(dateRange.startDate);
    const end = dayjs(dateRange.endDate);
    
    if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
      return start.format('MMM DD, YYYY');
    }
    
    return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
  };

  // Handle date filter change
  const handleDateFilterChange = (event) => {
    const selectedFilter = event.target.value;
    
    if (selectedFilter === 'Custom') {
      if (dateFilter === 'Custom') {
        const today = dayjs();
        if (dateRange.startDate && dateRange.endDate) {
          setCustomStartDate(dayjs(dateRange.startDate));
          setCustomEndDate(dayjs(dateRange.endDate));
        } else {
          setCustomStartDate(today.startOf('month'));
          setCustomEndDate(today.endOf('day'));
        }
        setCustomDateDialogOpen(true);
        return;
      }
      
      setDateFilter('Custom');
      const today = dayjs();
      if (dateRange.startDate && dateRange.endDate) {
        setCustomStartDate(dayjs(dateRange.startDate));
        setCustomEndDate(dayjs(dateRange.endDate));
      } else {
        setCustomStartDate(today.startOf('month'));
        setCustomEndDate(today.endOf('day'));
      }
      setCustomDateDialogOpen(true);
    } else {
      setDateFilter(selectedFilter);
      const newRange = calculateDateRange(selectedFilter);
      setDateRange(newRange);
    }
  };

  // Handle custom date apply
  const handleCustomDateApply = () => {
    const newRange = {
      startDate: customStartDate.format('YYYY-MM-DD'),
      endDate: customEndDate.format('YYYY-MM-DD')
    };
    setDateRange(newRange);
    setDateFilter('Custom');
    setCustomDateDialogOpen(false);
  };

  // Handle custom date dialog close
  const handleCustomDateDialogClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    setCustomDateDialogOpen(false);
  };

  // Fetch status counts
  const fetchStatusCounts = async () => {
    if (!agentId) return;
    setLoadingStatusCounts(true);
    try {
      const requestPayload = {
        date_range: {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        },
        agents: [parseInt(agentId)]
      };

      const response = await api.post('/lms/site-visits/status-counts/', requestPayload);
      if (response && response.data) {
        setStatusCounts(response.data);
      }
    } catch (error) {
      console.error('Error fetching status counts:', error);
    } finally {
      setLoadingStatusCounts(false);
    }
  };

  // Fetch lead performance data
  const fetchLeadPerformance = async () => {
    if (!agentId || !dateRange.startDate || !dateRange.endDate) return;
    setLoadingLeadPerformance(true);
    try {
      const requestPayload = {
        date_range: {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        }
      };

      // Use agent-specific performance endpoint for accurate counts
      const response = await api.post(`/report/agent/${agentId}/performance-report/lead-performance/`, requestPayload);
      if (response && response.data) {
        // Map the agent-specific response to our card structure
        // The agent endpoint returns: total_leads, new_leads, details_shared, 
        // site_visit_scheduled, site_visit_done, not_interested_leads, etc.
        setLeadPerformance(response.data);
      }
    } catch (error) {
      console.error('Error fetching lead performance:', error);
      // Set default values on error
      setLeadPerformance({});
    } finally {
      setLoadingLeadPerformance(false);
    }
  };

  // Handle card click navigation
  const handleCardClick = (statusId) => {
    const statusConfigs = {
      'all': { status: null },
      'scheduled': { status: 'scheduled' },
      'confirmed': { tags: ['visit confirmed'] },
      'completed': { status: 'completed' },
      'cancelled': { status: 'cancelled' }
    };

    const config = statusConfigs[statusId];
    if (!config) return;

    // Build query parameters
    const params = new URLSearchParams();
    params.append('agents', agentId);
    params.append('start_date', dateRange.startDate);
    params.append('end_date', dateRange.endDate);
    
    // Add date filter label to maintain the same filter selection
    params.append('date_filter', dateFilter);
    
    if (config.status) {
      params.append('status', config.status);
    }
    if (config.tags) {
      params.append('tags', config.tags.join(','));
    }

    navigate(`/site-visits?${params.toString()}`);
  };

  // Handle lead performance card click navigation
  const handleLeadCardClick = (statType) => {
    if (!statType || !dateRange.startDate || !dateRange.endDate) return;
    
    // Build query parameters for leads page
    const params = new URLSearchParams();
    params.set('stat_type', statType);
    params.set('start_date', dateRange.startDate);
    params.set('end_date', dateRange.endDate);
    params.set('assigned_to', agentId);
    
    // Navigate to leads page
    navigate(`/leads?${params.toString()}`);
  };


  // Error state
  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography color="error">Error loading site visits: {error.message || error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Date Filter and Status Cards */}
      <Paper sx={{ p: 3, boxShadow: '0 2px 5px rgba(0,0,0,0.05)', mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Site Visits Overview
          </Typography>
          
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateFilter}
              label="Date Range"
              onChange={handleDateFilterChange}
            >
              <MenuItem value="Today">
                <Box>
                  <Typography variant="body2">Today</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {dayjs().format('MMM DD, YYYY')}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="This Week">
                <Box>
                  <Typography variant="body2">This Week</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {`${dayjs().startOf('isoWeek').format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="This Month">
                <Box>
                  <Typography variant="body2">This Month</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {`${dayjs().startOf('month').format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="This Quarter">
                <Box>
                  <Typography variant="body2">This Quarter</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {(() => {
                      const currentQuarter = Math.floor(dayjs().month() / 3);
                      const quarterStartMonth = currentQuarter * 3;
                      const quarterStart = dayjs().month(quarterStartMonth).startOf('month');
                      return `${quarterStart.format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`;
                    })()}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem 
                value="Custom"
                onClick={(e) => {
                  e.stopPropagation();
                  if (dateFilter === 'Custom') {
                    e.preventDefault();
                    const today = dayjs();
                    if (dateRange.startDate && dateRange.endDate) {
                      setCustomStartDate(dayjs(dateRange.startDate));
                      setCustomEndDate(dayjs(dateRange.endDate));
                    } else {
                      setCustomStartDate(today.startOf('month'));
                      setCustomEndDate(today.endOf('day'));
                    }
                    setCustomDateDialogOpen(true);
                  }
                }}
              >
                <Box>
                  <Typography variant="body2">Custom</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {dateFilter === 'Custom' && getDateRangeDisplay() ? getDateRangeDisplay() : 'Select date range'}
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Status Cards */}
        <Box 
          sx={{ 
            mt: 3,
            display: { xs: 'grid', sm: 'grid', md: 'flex' },
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)'
            },
            gap: { xs: 2, sm: 2, md: 2 },
            flexWrap: { md: 'nowrap' },
          }}
        >
          {[
            { id: 'all', label: 'All Visit', color: '#0070F3', icon: <EventIcon /> },
            { id: 'scheduled', label: 'Scheduled', color: '#0891B2', icon: <ScheduleIcon /> },
            { id: 'confirmed', label: 'Confirmed', color: '#10B981', icon: <CheckCircleIcon /> },
            { id: 'completed', label: 'Completed', color: '#059669', icon: <DoneIcon /> },
            { id: 'cancelled', label: 'Cancelled', color: '#EF4444', icon: <CancelIcon /> }
          ].map((statusConfig, index) => {
            const statusData = statusCounts?.[statusConfig.id];
            const count = Number(statusData?.count ?? 0);
            const cardColor = statusConfig.color;

            return (
              <Box 
                key={statusConfig.id} 
                sx={{ 
                  minWidth: 0,
                  flex: { xs: 'none', sm: 'none', md: 1 },
                  flexBasis: { md: 0 },
                }}
              >
                <Paper
                  elevation={0}
                  onClick={() => handleCardClick(statusConfig.id)}
                  sx={{
                    py: 2.5,
                    px: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(cardColor, 0.15)}`,
                    background: 'background.paper',
                    boxShadow: `0 2px 8px ${alpha('#000', 0.08)}`,
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                    '&:hover': {
                      boxShadow: `0 4px 16px ${alpha(cardColor, 0.25)}, 0 2px 4px ${alpha(cardColor, 0.15)}`,
                      transform: 'translateY(-2px)',
                      border: `2px solid ${alpha(cardColor, 0.5)}`,
                      background: `linear-gradient(135deg, ${alpha(cardColor, 0.1)} 0%, ${alpha(cardColor, 0.05)} 100%)`,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1.5,
                        bgcolor: alpha(cardColor, 0.12),
                        color: cardColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {statusConfig.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'block',
                          mb: 0.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {statusConfig.label}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: cardColor,
                          fontSize: '1.75rem',
                          lineHeight: 1.2,
                        }}
                      >
                        {loadingStatusCounts ? '...' : count.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Lead Performance Cards */}
      <Paper sx={{ p: 3, boxShadow: '0 2px 5px rgba(0,0,0,0.05)', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Lead Performance
        </Typography>
        
        <Grid container spacing={3}>
          {[
            { 
              key: 'total_leads', 
              title: 'Total Leads', 
              icon: <TotalLeadsIcon />, 
              color: '#0f172a', 
              stat_type: 'total-leads' 
            },
            { 
              key: 'hot_leads', 
              title: 'Hot Leads', 
              icon: <HotLeadsIcon />, 
              color: '#dc2626', 
              stat_type: 'hot-leads' 
            },
            { 
              key: 'details_shared', 
              title: 'Details Shared', 
              icon: <DetailsIcon />, 
              color: '#ea580c', 
              stat_type: 'details-shared' 
            },
            { 
              key: 'site_visit_scheduled', 
              title: 'Site Visit Scheduled', 
              icon: <ScheduledVisitIcon />, 
              color: '#0891b2', 
              stat_type: 'site-visit-scheduled' 
            },
            { 
              key: 'site_visit_done', 
              title: 'Site Visit Done', 
              icon: <DoneIcon />, 
              color: '#f59e0b', 
              stat_type: 'site-visit-done' 
            },
            { 
              key: 'not_interested_leads', 
              title: 'Leads Marked Not Interested', 
              icon: <NotInterestedIcon />, 
              color: '#dc2626', 
              stat_type: 'not-interested-leads' 
            }
          ].map((leadConfig) => {
            const leadData = leadPerformance?.[leadConfig.key];
            const value = typeof leadData === 'object' && leadData?.value !== undefined 
              ? leadData.value 
              : (typeof leadData === 'number' ? leadData : 0);
            const cardColor = leadConfig.color;

            return (
              <Grid item xs={12} sm={6} md={4} key={leadConfig.key}>
                <Paper
                  elevation={0}
                  onClick={() => handleLeadCardClick(leadConfig.stat_type)}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${alpha(cardColor, 0.2)}`,
                    background: 'background.paper',
                    boxShadow: `0 2px 8px ${alpha(cardColor, 0.08)}`,
                    transition: 'all 0.3s ease',
                    height: '100%',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: `0 4px 16px ${alpha(cardColor, 0.15)}`,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          mb: 1,
                        }}
                      >
                        {leadConfig.title}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 'bold',
                          color: cardColor,
                          mb: 0.5,
                          fontSize: '1.75rem',
                        }}
                      >
                        {loadingLeadPerformance ? '...' : value.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(cardColor, 0.1),
                        color: cardColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {leadConfig.icon}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Custom Date Dialog */}
      <Dialog
        open={customDateDialogOpen}
        onClose={handleCustomDateDialogClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={false}
      >
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <DatePicker
                label="Start Date"
                value={customStartDate}
                onChange={(newValue) => setCustomStartDate(newValue)}
                maxDate={customEndDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={customEndDate}
                onChange={(newValue) => setCustomEndDate(newValue)}
                minDate={customStartDate}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCustomDateDialogClose}>Cancel</Button>
          <Button onClick={handleCustomDateApply} variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SiteVisits;
